import "./analytics";
import "@fontsource-variable/geist";
import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import { RouterProvider } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@diverge/ui/ui/tooltip";
import { router } from "./router";
import { handleChunkPreloadError } from "./utils/staleChunk";
import { createDashboardQueryClient } from "./utils/dashboardQueryClient";
import {
  installBrowserRuntimeEvents,
  reportBrowserRuntimeError,
} from "@diverge/observability/browser";
import { dashboardObservability } from "./utils/observability";

installBrowserRuntimeEvents(dashboardObservability);
dashboardObservability.addBreadcrumb("dashboard.bootstrap.started");

// Vite raises this when a code-split chunk (route pages, the form flow diagram)
// can't be fetched — almost always because a deploy replaced the hashed assets
// this tab was built against. Reload onto the current build. The event is left
// un-prevented on purpose: Vite would then resolve the import with `undefined`
// and the caller would trip over that instead of seeing the real failure.
window.addEventListener("vite:preloadError", handleChunkPreloadError);

const queryClient = createDashboardQueryClient();

// Render failures are reported like any other runtime error: one sanitized
// record, delivered to the operational logs (Grafana) through the API.
const root = ReactDOM.createRoot(document.getElementById("root")!, {
  // Thrown and not caught by an ErrorBoundary.
  onUncaughtError: (error) => reportBrowserRuntimeError(dashboardObservability, error, "render"),
  // Caught by an ErrorBoundary.
  onCaughtError: (error) => reportBrowserRuntimeError(dashboardObservability, error, "render"),
  // React recovered by itself.
  onRecoverableError: (error) =>
    reportBrowserRuntimeError(dashboardObservability, error, "render", true),
});

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <RouterProvider router={router} />
      </TooltipProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
